import React from "react";

import { calculateAge } from "@/utils/momentUtils";
import { cn } from "@/lib/helpers/cn";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { formatMobileNumber } from "@/utils/filters";
import { Heading2 } from "@/ui2/atoms/Heading";
import { usePatientDetails } from "@/features/patient/api/queries/usePatientDetails";
import { usePatientDiagnosis } from "@/features/patient/api/queries/usePatientDiagnosis";
import type { UUID } from "@/core/types";

type Props = {
  className?: string;
  patientId: UUID;
};

export function MemberHeader({ className, patientId }: Props) {
  const { data: patient } = usePatientDetails({ patientId });
  const { data: diagnosis } = usePatientDiagnosis({ patientId });

  if (!patient || !diagnosis) return null;

  const primaryDiagnosis =
    (diagnosis.ICD_10_axis_1_diagnosis ?? []).map((d) => d.value).join(", ") || "None";
  const secondaryDiagnosis =
    (diagnosis.ICD_10_axis_2_diagnosis ?? []).map((d) => d.value).join(", ") || "None";

  return (
    <DashboardCard2
      as="section"
      className={cn("tw:w-full tw:p-5!", className)}
      classNameChildren="tw:flex tw:justify-center tw:p-0 tw:gap-6"
    >
      <div>
        <Heading2 as="h2" data-private size={3}>
          {patient.first_name} {patient.last_name}
        </Heading2>

        <div className="tw:w-max">
          <span className="tw:text-sm tw:text-text-tertiary">
            {patient.dob} ({calculateAge(patient.dob ?? "")})
          </span>

          <span> • </span>
          <span className="tw:text-sm tw:text-text-tertiary">
            {formatMobileNumber(patient.mobile)}
          </span>

          <span> • </span>
          <span className="tw:text-sm tw:text-text-tertiary">
            Day {patient.number_of_days_in_care} of care
          </span>
        </div>
      </div>

      <div className="vertical-break" />

      <dl className="tw:flex tw:flex-col tw:gap-2">
        <div>
          <dt className="tw:mr-ch tw:inline tw:font-bold">Primary Diagnosis: </dt>
          <dd className="tw:inline">{primaryDiagnosis}</dd>
        </div>

        <div>
          <dt className="tw:mr-ch tw:inline tw:font-bold">Secondary Diagnosis: </dt>
          <dd className="tw:inline">{secondaryDiagnosis}</dd>
        </div>
      </dl>
    </DashboardCard2>
  );
}
